import ReactGA from 'react-ga4';

const MEASUREMENT_ID = 'G-X9LBW6KP2G';

// Check if this is a test visit (URL contains ?test=true or was marked as test)
const checkIfTestVisit = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const isTestFromUrl = urlParams.get('test') === 'true';
  
  // If URL has test=true, store it in sessionStorage for the entire session
  if (isTestFromUrl) {
    sessionStorage.setItem('analytics_test_mode', 'true');
  }
  
  // Return true if either URL param or sessionStorage indicates test mode
  return isTestFromUrl || sessionStorage.getItem('analytics_test_mode') === 'true';
};

// Export function to check test mode (useful for debugging)
export const isTestMode = () => checkIfTestVisit();

// Get traffic type for GA4
const getTrafficType = () => checkIfTestVisit() ? 'test' : 'real';

// Initialize Google Analytics
export const initGA = () => {
  // Only initialize in production to avoid tracking during development
  if (process.env.NODE_ENV === 'production') {
    const isTest = checkIfTestVisit();
    
    ReactGA.initialize(MEASUREMENT_ID, {
      gtagOptions: {
        // Enable enhanced measurement features
        send_page_view: false, // We'll send it manually with traffic_type
        // Set user property for traffic type
        user_properties: {
          traffic_type: getTrafficType(),
        },
      },
    });
    
    // Set the traffic_type as a persistent user property
    ReactGA.gtag('set', 'user_properties', {
      traffic_type: getTrafficType(),
    });
    
    console.log(`Analytics initialized (${isTest ? 'TEST MODE' : 'PRODUCTION'})`);
  }
};

// Track page views
export const trackPageView = (path = window.location.pathname) => {
  ReactGA.send({
    hitType: 'pageview',
    page: path,
    title: document.title,
    traffic_type: getTrafficType(),
  });
};

// Track scroll depth (percentage of page scrolled)
export const trackScrollDepth = (percentage) => {
  ReactGA.event({
    category: 'Engagement',
    action: 'Scroll Depth',
    label: `${percentage}%`,
    value: percentage,
    traffic_type: getTrafficType(),
  });
};

// Track time spent on page
export const trackTimeOnPage = (seconds) => {
  ReactGA.event({
    category: 'Engagement',
    action: 'Time on Page',
    label: `${seconds} seconds`,
    value: seconds,
    traffic_type: getTrafficType(),
  });
};

// Track gallery interactions
export const trackGalleryClick = (imageIndex) => {
  ReactGA.event({
    category: 'Gallery',
    action: 'Image Click',
    label: `Image ${imageIndex + 1}`,
    value: imageIndex,
    traffic_type: getTrafficType(),
  });
};

// Track lightbox navigation. `source` names the set being browsed ('Gallery',
// 'Design', 'Plan') and rides in the event name so the sets can be told apart
// in GA4's default Events report without registering a custom dimension.
export const trackLightboxNavigation = (direction, newIndex, source = 'Gallery') => {
  ReactGA.event({
    category: 'Gallery',
    action: `${source} Lightbox ${direction}`,
    label: `To Image ${newIndex + 1}`,
    traffic_type: getTrafficType(),
  });
};

// Track floor plan opens
export const trackFloorPlanOpen = () => {
  ReactGA.event({
    category: 'Gallery',
    action: 'Floor Plan Open',
    traffic_type: getTrafficType(),
  });
};

// Track design project (3D renders) opens
export const trackDesignProjectClick = (imageIndex) => {
  ReactGA.event({
    category: 'Gallery',
    action: 'Design Project Click',
    label: `Render ${imageIndex + 1}`,
    value: imageIndex,
    traffic_type: getTrafficType(),
  });
};

// Track lightbox close
export const trackLightboxClose = (imageIndex, source = 'Gallery') => {
  ReactGA.event({
    category: 'Gallery',
    action: `${source} Lightbox Close`,
    label: `From Image ${imageIndex + 1}`,
    traffic_type: getTrafficType(),
  });
};

// How many distinct images from a set someone actually looked at before
// closing. Opening and bouncing scores 1; genuine interest scores high.
export const trackImagesViewed = (source, viewedCount, total) => {
  ReactGA.event({
    category: 'Engagement',
    action: `${source} Images Viewed`,
    label: `${viewedCount} of ${total}`,
    value: viewedCount,
    traffic_type: getTrafficType(),
  });
};

// Track phone clicks
export const trackPhoneClick = (location) => {
  ReactGA.event({
    category: 'Contact',
    action: 'Phone Click',
    label: location, // e.g., 'header', 'contact-section'
    traffic_type: getTrafficType(),
  });
};

// Track WhatsApp clicks
export const trackWhatsAppClick = (location) => {
  ReactGA.event({
    category: 'Contact',
    action: 'WhatsApp Click',
    label: location, // e.g. 'header', 'contact-section'
    traffic_type: getTrafficType(),
  });
};

// Track section views (when user scrolls to a section)
export const trackSectionView = (sectionName) => {
  ReactGA.event({
    category: 'Engagement',
    action: 'Section View',
    label: sectionName,
    traffic_type: getTrafficType(),
  });
};

// Track outbound links
export const trackOutboundLink = (url, label) => {
  ReactGA.event({
    category: 'Outbound',
    action: 'Click',
    label: label || url,
    traffic_type: getTrafficType(),
  });
};

// Custom hook for scroll tracking
export const useScrollTracking = () => {
  const scrollMilestones = [25, 50, 75, 90, 100];
  const trackedMilestones = new Set();

  const handleScroll = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercent = Math.round((scrollTop / docHeight) * 100);

    scrollMilestones.forEach((milestone) => {
      if (scrollPercent >= milestone && !trackedMilestones.has(milestone)) {
        trackedMilestones.add(milestone);
        trackScrollDepth(milestone);
      }
    });
  };

  return { handleScroll, trackedMilestones };
};

// Section visibility tracking (Intersection Observer based)
export const createSectionObserver = (callback) => {
  const observedSections = new Set();
  
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionName = entry.target.dataset.section;
          if (sectionName && !observedSections.has(sectionName)) {
            observedSections.add(sectionName);
            callback(sectionName);
          }
        }
      });
    },
    { threshold: 0.5 } // Trigger when 50% of section is visible
  );

  return observer;
};
